import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  EmissionFactor,
  UpsertEmissionFactorRequest
} from '../../types/emission';

interface Props {
  vehicleTypeValue: string;
  vehicleTypeLabel: string;
  factor?: EmissionFactor | null;
  onSave: (vehicleType: string, data: UpsertEmissionFactorRequest) => void;
  onCancel: () => void;
  loading: boolean;
}

const emptyForm = {
  gramsCo2ePerPassengerKm: '', unit: 'g/Pkm',
  source: '', description: '', validFrom: '', validTo: '', isActive: true
};

const EmissionFactorForm: React.FC<Props> = ({ vehicleTypeValue, vehicleTypeLabel, factor, onSave, onCancel, loading }) => {
  const { t } = useTranslation(['common']);
  const [form, setForm] = useState({ ...emptyForm });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (factor) {
      setForm({
        gramsCo2ePerPassengerKm: factor.gramsCo2ePerPassengerKm?.toString() ?? '',
        unit: factor.unit || 'g/Pkm',
        source: factor.source || '',
        description: factor.description || '',
        validFrom: factor.validFrom ? factor.validFrom.substring(0, 10) : '',
        validTo: factor.validTo ? factor.validTo.substring(0, 10) : '',
        isActive: factor.isActive
      });
    } else {
      setForm({ ...emptyForm });
    }
  }, [factor]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};
    if (form.gramsCo2ePerPassengerKm === '' || isNaN(Number(form.gramsCo2ePerPassengerKm)) || Number(form.gramsCo2ePerPassengerKm) < 0)
      newErrors.gramsCo2ePerPassengerKm = t('common:emissionFactors.errors.factorInvalid', 'Emissionsfaktor muss >= 0 sein');
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    onSave(vehicleTypeValue, {
      gramsCo2ePerPassengerKm: Number(form.gramsCo2ePerPassengerKm),
      unit: form.unit.trim() || 'g/Pkm',
      source: form.source.trim() || undefined,
      description: form.description.trim() || undefined,
      validFrom: form.validFrom || null,
      validTo: form.validTo || null,
      isActive: form.isActive
    });
  };

  const input: React.CSSProperties = { width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.875rem' };
  const label: React.CSSProperties = { fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.25rem', display: 'block' };
  const group: React.CSSProperties = { marginBottom: '0.75rem' };

  return (
    <div style={overlay}>
      <div style={modal}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginTop: 0 }}>
          {t('common:emissionFactors.editTitle', 'Emissionsfaktor bearbeiten')} – {vehicleTypeLabel}
        </h2>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <div style={{ ...group, flex: 1 }}>
              <label style={label}>{t('common:emissionFactors.vehicleType', 'Verkehrsmittel')}</label>
              <input value={vehicleTypeLabel} style={{ ...input, background: '#f9fafb' }} disabled readOnly />
            </div>
            <div style={{ ...group, flex: 1 }}>
              <label style={label}>{t('common:emissionFactors.gramsPerPassengerKm', 'g CO₂e / Pkm')} *</label>
              <input type="number" name="gramsCo2ePerPassengerKm" value={form.gramsCo2ePerPassengerKm} onChange={handleChange} style={input} step="0.01" min="0" disabled={loading} />
              {errors.gramsCo2ePerPassengerKm && <div style={{ color: '#ef4444', fontSize: '0.75rem' }}>{errors.gramsCo2ePerPassengerKm}</div>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <div style={{ ...group, flex: 1 }}>
              <label style={label}>{t('common:emissionFactors.validFrom', 'Gültig ab')}</label>
              <input type="date" name="validFrom" value={form.validFrom} onChange={handleChange} style={input} disabled={loading} />
            </div>
            <div style={{ ...group, flex: 1 }}>
              <label style={label}>{t('common:emissionFactors.validTo', 'Gültig bis')}</label>
              <input type="date" name="validTo" value={form.validTo} onChange={handleChange} style={input} disabled={loading} />
            </div>
          </div>
          <div style={group}>
            <label style={label}>{t('common:emissionFactors.source', 'Quelle')}</label>
            <input name="source" value={form.source} onChange={handleChange} style={input} disabled={loading} placeholder="z.B. UBA 2024" />
          </div>
          <div style={group}>
            <label style={label}>{t('common:description', 'Beschreibung')}</label>
            <textarea name="description" value={form.description} onChange={handleChange} style={{ ...input, minHeight: 60 }} disabled={loading} />
          </div>
          <div style={group}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" name="isActive" checked={form.isActive} onChange={handleChange} disabled={loading} />
              {t('common:active', 'Aktiv')}
            </label>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1rem' }}>
            <button type="button" onClick={onCancel} disabled={loading} style={{ padding: '0.5rem 1rem', border: '1px solid #d1d5db', borderRadius: 6, background: 'white', cursor: 'pointer' }}>
              {t('common:cancel', 'Abbrechen')}
            </button>
            <button type="submit" disabled={loading} style={{ padding: '0.5rem 1rem', border: 'none', borderRadius: 6, background: '#3b82f6', color: 'white', cursor: 'pointer' }}>
              {loading ? t('common:loading', 'Lädt...') : (factor ? t('common:update', 'Aktualisieren') : t('common:create', 'Erstellen'))}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const overlay: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modal: React.CSSProperties = { background: 'white', borderRadius: 12, padding: '1.5rem', width: '90%', maxWidth: 560, maxHeight: '90vh', overflow: 'auto' };

export default EmissionFactorForm;
